import { authedFetch } from '../lib/authedFetch';
import {
  ECONOMIC_RESEARCH_FACETS,
  ECONOMIC_RESEARCH_SCHEMA_VERSION,
  type CreateEconomicResearchRunRequest,
  type CreateEconomicResearchRunResponse,
  type EconomicResearchComparableSummary,
  type EconomicResearchEvidenceStep,
  type EconomicResearchRunCounts,
  type EconomicResearchRunView,
  type EconomicResearchObjective,
  type EconomicResearchSourceFailureKind,
  type EconomicResearchStage,
  type EconomicResearchTerminalResult,
} from '../types/economicResearch';
import type { EconomicSourceRef } from '../types/economicContribution';

const ROUTE_BASE = '/api/economic-contribution/research-runs';
const STAGES = new Set<EconomicResearchStage>([
  'queued',
  'discovering',
  'retrieving',
  'extracting',
  'validating',
  'complete',
]);
const TERMINAL_RESULTS = new Set<EconomicResearchTerminalResult>([
  'complete-with-evidence',
  'complete-no-policy-valid-evidence',
  'complete-needs-context',
  'failed',
]);
const OBJECTIVE_KINDS = new Set(['discover-comparable-cohort', 'enrich-comparable-project', 'refresh-comparable-project']);
const RESEARCH_FACETS = new Set<string>(ECONOMIC_RESEARCH_FACETS);
const FACT_MW_BASES = new Set(['it', 'facility', 'utility-service', 'backup-generation']);
const FACT_EVIDENCE_BASES = new Set(['verified-actual', 'observed-public-record', 'committed', 'announced', 'secondary-discovery']);
const EVIDENCE_STEP_KINDS = new Set(['official-record', 'extracted-claim', 'compatibility-decision']);
const SOURCE_EXCERPT_KINDS = new Set(['verbatim-quote', 'table-transcription']);
const SOURCE_EVIDENCE_ROLES = new Set(['numeric-basis', 'independent-cross-check']);
const SOURCE_FAILURE_KINDS = new Set<EconomicResearchSourceFailureKind>([
  'schema-drift',
  'availability',
  'other',
]);
const NORMALIZED_CHECK_USES = new Set([
  'operations-fte-per-mw',
  'construction-peak-workers-per-mw',
  'construction-average-to-peak-ratio',
  'construction-duration-years',
]);
const NORMALIZED_CHECK_UNITS = new Set(['workers-per-utility-service-mw', 'ratio', 'years']);

interface EconomicResearchClientOptions {
  signal?: AbortSignal;
  fetcher?: typeof authedFetch;
}

function record(value: unknown): Record<string, unknown> | null {
  return value != null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function nonemptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function safeHttpsUrl(value: unknown): value is string {
  if (!nonemptyString(value)) return false;
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
}

function optionalString(value: unknown): value is string | undefined {
  return value === undefined || typeof value === 'string';
}

function parseEconomicSourceRef(value: unknown): EconomicSourceRef {
  const source = record(value);
  if (
    !source
    || !nonemptyString(source.id)
    || !nonemptyString(source.title)
    || !nonemptyString(source.publisher)
    || !safeHttpsUrl(source.url)
    || !nonemptyString(source.accessedAt)
    || !nonemptyString(source.excerpt)
    || !nonemptyString(source.locator)
    || typeof source.excerptKind !== 'string'
    || !SOURCE_EXCERPT_KINDS.has(source.excerptKind)
    || typeof source.evidenceRole !== 'string'
    || !SOURCE_EVIDENCE_ROLES.has(source.evidenceRole)
    || !optionalString(source.publicationDate)
    || !optionalString(source.page)
    || !optionalString(source.notes)
  ) {
    throw new Error('The economic research service returned an invalid comparable-fact source.');
  }
  const normalizedChecks = source.normalizedChecks;
  if (normalizedChecks !== undefined && (
    !Array.isArray(normalizedChecks)
    || normalizedChecks.some((value) => {
      const check = record(value);
      return !check
        || typeof check.use !== 'string'
        || !NORMALIZED_CHECK_USES.has(check.use)
        || typeof check.units !== 'string'
        || !NORMALIZED_CHECK_UNITS.has(check.units)
        || typeof check.low !== 'number'
        || !Number.isFinite(check.low)
        || typeof check.high !== 'number'
        || !Number.isFinite(check.high)
        || check.low > check.high
        || (check.mw !== undefined && (typeof check.mw !== 'number' || !Number.isFinite(check.mw)));
    })
  )) {
    throw new Error('The economic research service returned invalid comparable-fact source checks.');
  }
  return {
    id: source.id,
    title: source.title,
    publisher: source.publisher,
    url: source.url,
    ...(source.publicationDate !== undefined ? { publicationDate: source.publicationDate } : {}),
    accessedAt: source.accessedAt,
    excerpt: source.excerpt,
    locator: source.locator,
    excerptKind: source.excerptKind as EconomicSourceRef['excerptKind'],
    evidenceRole: source.evidenceRole as EconomicSourceRef['evidenceRole'],
    ...(normalizedChecks !== undefined ? { normalizedChecks: normalizedChecks as NonNullable<EconomicSourceRef['normalizedChecks']> } : {}),
    ...(source.page !== undefined ? { page: source.page } : {}),
    ...(source.notes !== undefined ? { notes: source.notes } : {}),
  };
}

function parseFactSources(value: unknown): EconomicSourceRef[] {
  // Legacy v1 snapshots did not associate sources with individual scalars.
  // Migrate them to an empty list; never guess from the project-wide trail.
  if (value === undefined) return [];
  if (!Array.isArray(value)) {
    throw new Error('The economic research service returned invalid comparable-fact sources.');
  }
  return value.map(parseEconomicSourceRef);
}

function parseEvidenceStep(value: unknown): EconomicResearchEvidenceStep {
  const step = record(value);
  if (
    !step
    || typeof step.kind !== 'string'
    || !EVIDENCE_STEP_KINDS.has(step.kind)
    || !nonemptyString(step.label)
    || !nonemptyString(step.detail)
    || (step.sourceUrl !== undefined && !safeHttpsUrl(step.sourceUrl))
    || !optionalString(step.excerpt)
    || !optionalString(step.locator)
  ) {
    throw new Error('The economic research service returned an invalid comparable evidence step.');
  }
  return {
    kind: step.kind as EconomicResearchEvidenceStep['kind'],
    label: step.label,
    detail: step.detail,
    ...(step.sourceUrl !== undefined ? { sourceUrl: step.sourceUrl } : {}),
    ...(step.excerpt !== undefined ? { excerpt: step.excerpt } : {}),
    ...(step.locator !== undefined ? { locator: step.locator } : {}),
  };
}

function parseComparableSummary(value: unknown): EconomicResearchComparableSummary {
  const summary = record(value);
  if (
    !summary
    || !nonemptyString(summary.projectId)
    || !nonemptyString(summary.facilityName)
    || !nonemptyString(summary.stateCode)
    || typeof summary.score !== 'number'
    || !Number.isFinite(summary.score)
    || !stringArray(summary.reasons)
    || !stringArray(summary.incompatibilities)
    || typeof summary.compatibleForJobsPerMw !== 'boolean'
    || !Array.isArray(summary.evidenceTrail)
  ) {
    throw new Error('The economic research service returned an invalid comparable summary.');
  }
  const mwCandidate = summary.mw === undefined ? null : record(summary.mw);
  if (summary.mw !== undefined && (
    !mwCandidate
    || typeof mwCandidate.value !== 'number'
    || !Number.isFinite(mwCandidate.value)
    || mwCandidate.value < 0
    || typeof mwCandidate.basis !== 'string'
    || !FACT_MW_BASES.has(mwCandidate.basis)
    || !nonemptyString(mwCandidate.phaseBasis)
  )) {
    throw new Error('The economic research service returned an invalid comparable MW fact.');
  }
  const jobsCandidate = summary.permanentJobs === undefined ? null : record(summary.permanentJobs);
  if (summary.permanentJobs !== undefined && (
    !jobsCandidate
    || typeof jobsCandidate.low !== 'number'
    || !Number.isFinite(jobsCandidate.low)
    || jobsCandidate.low < 0
    || typeof jobsCandidate.high !== 'number'
    || !Number.isFinite(jobsCandidate.high)
    || jobsCandidate.high < jobsCandidate.low
    || typeof jobsCandidate.evidenceBasis !== 'string'
    || !FACT_EVIDENCE_BASES.has(jobsCandidate.evidenceBasis)
    || !nonemptyString(jobsCandidate.phaseBasis)
  )) {
    throw new Error('The economic research service returned an invalid comparable jobs fact.');
  }
  const mw = mwCandidate ? {
    value: mwCandidate.value as number,
    basis: mwCandidate.basis as NonNullable<EconomicResearchComparableSummary['mw']>['basis'],
    phaseBasis: mwCandidate.phaseBasis as string,
    sources: parseFactSources(mwCandidate.sources),
  } : undefined;
  const permanentJobs = jobsCandidate ? {
    low: jobsCandidate.low as number,
    high: jobsCandidate.high as number,
    evidenceBasis: jobsCandidate.evidenceBasis as NonNullable<EconomicResearchComparableSummary['permanentJobs']>['evidenceBasis'],
    phaseBasis: jobsCandidate.phaseBasis as string,
    sources: parseFactSources(jobsCandidate.sources),
  } : undefined;
  return {
    ...(summary as unknown as EconomicResearchComparableSummary),
    ...(mw ? { mw } : {}),
    ...(permanentJobs ? { permanentJobs } : {}),
    evidenceTrail: summary.evidenceTrail.map(parseEvidenceStep),
  };
}

async function responseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return { error: text };
  }
}

function errorMessage(body: unknown, fallback: string): string {
  const message = record(body)?.error;
  return typeof message === 'string' && message.trim() ? message : fallback;
}

function finiteNonnegativeInteger(value: unknown): value is number {
  return Number.isSafeInteger(value) && Number(value) >= 0;
}

function parseCounts(value: unknown): EconomicResearchRunCounts {
  const candidate = record(value);
  const keys = [
    'sourcesAttempted',
    'candidatesFound',
    'recordsPromoted',
    'recordsHeld',
    'recordsExcluded',
    'cacheHits',
  ] as const;
  if (!candidate || keys.some((key) => !finiteNonnegativeInteger(candidate[key]))) {
    throw new Error('The economic research service returned invalid result counts.');
  }
  const counts = Object.fromEntries(keys.map((key) => [key, Number(candidate[key])])) as unknown as EconomicResearchRunCounts;
  const parsed = {
    ...counts,
    // Runs from before the evidence-change counter cannot prove that a
    // promotion changed the registry, so they fail closed to zero.
    recordsChanged: finiteNonnegativeInteger(candidate.recordsChanged)
      ? Number(candidate.recordsChanged)
      : 0,
  };
  if ((parsed.recordsChanged ?? 0) > parsed.recordsPromoted) {
    throw new Error('The economic research service returned inconsistent result counts.');
  }
  return parsed;
}

function stringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === 'string');
}

function parseObjective(value: unknown): EconomicResearchObjective {
  const candidate = record(value);
  if (
    !candidate
    || typeof candidate.kind !== 'string'
    || !OBJECTIVE_KINDS.has(candidate.kind)
    || !Array.isArray(candidate.requestedFacets)
    || candidate.requestedFacets.length < 1
    || candidate.requestedFacets.some((facet) => typeof facet !== 'string' || !RESEARCH_FACETS.has(facet))
    || new Set(candidate.requestedFacets).size !== candidate.requestedFacets.length
  ) {
    throw new Error('The economic research service returned an invalid objective.');
  }
  const projectId = typeof candidate.projectId === 'string' && candidate.projectId.trim()
    ? candidate.projectId.trim()
    : undefined;
  if (
    (candidate.kind === 'discover-comparable-cohort' && projectId)
    || (candidate.kind !== 'discover-comparable-cohort' && !projectId)
  ) {
    throw new Error('The economic research service returned an invalid objective target.');
  }
  return {
    kind: candidate.kind as EconomicResearchObjective['kind'],
    requestedFacets: candidate.requestedFacets as EconomicResearchObjective['requestedFacets'],
    ...(projectId ? { projectId } : {}),
  };
}

function parseRunView(value: unknown): EconomicResearchRunView {
  const candidate = record(value);
  if (
    !candidate
    || candidate.schemaVersion !== ECONOMIC_RESEARCH_SCHEMA_VERSION
    || typeof candidate.runId !== 'string'
    || candidate.runId.length === 0
    || typeof candidate.siteId !== 'string'
    || candidate.siteId.length === 0
    || typeof candidate.siteFingerprint !== 'string'
    || candidate.siteFingerprint.length === 0
    || typeof candidate.stage !== 'string'
    || !STAGES.has(candidate.stage as EconomicResearchStage)
    || typeof candidate.progress !== 'number'
    || !Number.isFinite(candidate.progress)
    || candidate.progress < 0
    || candidate.progress > 100
    || typeof candidate.currentStageLabel !== 'string'
    || !stringArray(candidate.sourcesAttempted)
    || typeof candidate.createdAt !== 'string'
    || typeof candidate.updatedAt !== 'string'
    || typeof candidate.staleAt !== 'string'
    || typeof candidate.retryable !== 'boolean'
  ) {
    throw new Error('The economic research service returned an invalid run.');
  }
  if (
    candidate.terminalResult !== undefined
    && (
      typeof candidate.terminalResult !== 'string'
      || !TERMINAL_RESULTS.has(candidate.terminalResult as EconomicResearchTerminalResult)
    )
  ) {
    throw new Error('The economic research service returned an invalid terminal result.');
  }
  if (candidate.stage === 'complete' && candidate.terminalResult === undefined) {
    throw new Error('The economic research service returned a completed run without a result.');
  }
  const counts = parseCounts(candidate.counts);
  const objective = candidate.objective === undefined ? undefined : parseObjective(candidate.objective);
  const resultSnapshot = candidate.resultSnapshot;
  let parsedResultSnapshot: EconomicResearchRunView['resultSnapshot'];
  if (resultSnapshot !== undefined) {
    const snapshot = record(resultSnapshot);
    if (
      !snapshot
      || snapshot.schemaVersion !== ECONOMIC_RESEARCH_SCHEMA_VERSION
      || typeof snapshot.snapshotId !== 'string'
      || typeof snapshot.siteFingerprint !== 'string'
      || typeof snapshot.generatedAt !== 'string'
      || !Array.isArray(snapshot.policyValidRecords)
      || !Array.isArray(snapshot.held)
      || !Array.isArray(snapshot.excluded)
      || !Array.isArray(snapshot.fiscalBaselineDrafts)
    ) {
      throw new Error('The economic research service returned an invalid result snapshot.');
    }
    const rawSourceFailures = snapshot.sourceFailures;
    if (rawSourceFailures !== undefined && !Array.isArray(rawSourceFailures)) {
      throw new Error('The economic research service returned invalid source-failure details.');
    }
    const parsedSourceFailures = (rawSourceFailures ?? []).map((value) => {
      const failure = record(value);
      if (
        !failure
        || typeof failure.sourceId !== 'string'
        || !failure.sourceId.trim()
        || typeof failure.sourceClass !== 'string'
        || !failure.sourceClass.trim()
        || (failure.failureKind !== undefined && (
          typeof failure.failureKind !== 'string'
          || !SOURCE_FAILURE_KINDS.has(failure.failureKind as EconomicResearchSourceFailureKind)
        ))
      ) {
        throw new Error('The economic research service returned invalid source-failure details.');
      }
      return {
        sourceId: failure.sourceId,
        sourceClass: failure.sourceClass,
        // Migrate metadata-only legacy snapshots without guessing a cause.
        failureKind: typeof failure.failureKind === 'string'
          ? failure.failureKind as EconomicResearchSourceFailureKind
          : 'other',
      };
    });
    parsedResultSnapshot = {
      ...(snapshot as unknown as NonNullable<EconomicResearchRunView['resultSnapshot']>),
      ...(rawSourceFailures !== undefined ? { sourceFailures: parsedSourceFailures } : {}),
      policyValidRecords: snapshot.policyValidRecords.map(parseComparableSummary),
    };
  }
  return {
    schemaVersion: ECONOMIC_RESEARCH_SCHEMA_VERSION,
    runId: candidate.runId,
    siteId: candidate.siteId,
    siteFingerprint: candidate.siteFingerprint,
    ...(objective ? { objective } : {}),
    stage: candidate.stage as EconomicResearchStage,
    ...(candidate.terminalResult
      ? { terminalResult: candidate.terminalResult as EconomicResearchTerminalResult }
      : {}),
    progress: candidate.progress,
    currentStageLabel: candidate.currentStageLabel,
    ...(typeof candidate.currentSourceLabel === 'string'
      ? { currentSourceLabel: candidate.currentSourceLabel }
      : {}),
    sourcesAttempted: candidate.sourcesAttempted,
    counts,
    createdAt: candidate.createdAt,
    updatedAt: candidate.updatedAt,
    ...(typeof candidate.finishedAt === 'string' ? { finishedAt: candidate.finishedAt } : {}),
    staleAt: candidate.staleAt,
    ...(typeof candidate.failedSourceClass === 'string'
      ? { failedSourceClass: candidate.failedSourceClass }
      : {}),
    retryable: candidate.retryable,
    ...(parsedResultSnapshot !== undefined
      ? { resultSnapshot: parsedResultSnapshot }
      : {}),
  };
}

function parseRunEnvelope(value: unknown): { run: EconomicResearchRunView; duplicate?: boolean } {
  const envelope = record(value);
  if (!envelope || envelope.run === undefined) {
    throw new Error('The economic research service returned an invalid response.');
  }
  if (envelope.duplicate !== undefined && typeof envelope.duplicate !== 'boolean') {
    throw new Error('The economic research service returned an invalid duplicate marker.');
  }
  return {
    run: parseRunView(envelope.run),
    ...(typeof envelope.duplicate === 'boolean' ? { duplicate: envelope.duplicate } : {}),
  };
}

export async function createEconomicResearchRun(
  request: CreateEconomicResearchRunRequest,
  options: EconomicResearchClientOptions = {},
): Promise<CreateEconomicResearchRunResponse> {
  const response = await (options.fetcher ?? authedFetch)(ROUTE_BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
    signal: options.signal,
  });
  const body = await responseBody(response);
  if (!response.ok) {
    throw new Error(errorMessage(body, `Public-record research could not start (${response.status}).`));
  }
  const parsed = parseRunEnvelope(body);
  return { run: parsed.run, duplicate: parsed.duplicate ?? false };
}

export async function getEconomicResearchRun(
  runId: string,
  options: EconomicResearchClientOptions = {},
): Promise<EconomicResearchRunView> {
  const response = await (options.fetcher ?? authedFetch)(`${ROUTE_BASE}/${encodeURIComponent(runId)}`, {
    method: 'GET',
    signal: options.signal,
  });
  const body = await responseBody(response);
  if (!response.ok) {
    throw new Error(errorMessage(body, `Public-record research status could not be loaded (${response.status}).`));
  }
  return parseRunEnvelope(body).run;
}

export async function refreshEconomicResearchRun(
  runId: string,
  options: EconomicResearchClientOptions = {},
): Promise<CreateEconomicResearchRunResponse> {
  const response = await (options.fetcher ?? authedFetch)(`${ROUTE_BASE}/${encodeURIComponent(runId)}/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
    signal: options.signal,
  });
  const body = await responseBody(response);
  if (!response.ok) {
    throw new Error(errorMessage(body, `Public-record research could not be refreshed (${response.status}).`));
  }
  const parsed = parseRunEnvelope(body);
  return { run: parsed.run, duplicate: parsed.duplicate ?? false };
}

export function isEconomicResearchRunActive(run: EconomicResearchRunView | null | undefined): boolean {
  return Boolean(run && run.stage !== 'complete' && run.terminalResult === undefined);
}
